/**
 * @fileoverview Collapsible "Items to Review" panel for the active form.
 * @module inspection-renderer/validation
 *
 * Responsibilities:
 * - Render a single banner summarizing every open item on the active form
 *   (required-missing, range/pattern errors, ack required, unmet notes /
 *   photos, etc.).
 * - Each row is a `<button>` that scrolls to the offending field via
 *   `[data-field-id="..."]`.
 *
 * Dependencies:
 * - React 18 (peer).
 * - `@firesquirrel/core-domain` types.
 * - `./fieldIssues.ts` for the canonical predicates.
 *
 * Architecture:
 * - Pure presentation. Caller computes the items; this just renders.
 *
 * Notes:
 * - Renders nothing when there are no items. Hosts therefore don't need
 *   to gate it themselves.
 *
 * @component ValidationSummary
 *
 * Props:
 * - issues: aggregated FieldIssue[] for the active form.
 *
 * Behavior:
 * - Collapsible header with a "X item(s) to review" count chip.
 * - Click a row → scrolls the field into view + focuses it.
 *
 * Constraints:
 * - No state beyond local "collapsed" toggle.
 */
import { useState, type ReactNode } from 'react';
import { type FieldIssue, describeIssueKind } from './fieldIssues';

export interface ValidationSummaryProps {
  issues: FieldIssue[];
}

function focusField(fieldId: string): void {
  if (typeof document === 'undefined') return;
  const node = document.querySelector(`[data-field-id="${fieldId}"]`);
  if (!node) return;
  // Smooth-scroll the wrapper into view.
  if (typeof (node as Element).scrollIntoView === 'function') {
    (node as Element).scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  // Try to focus the inner control. The wrapper assigns `id={field.id}` to
  // its primary input; fall back to the wrapper itself. We use getElementById
  // so the lookup works without `CSS.escape` (not available in JSDOM).
  const input = document.getElementById(fieldId);
  if (input) {
    setTimeout(() => input.focus({ preventScroll: true }), 0);
  } else {
    (node as HTMLElement).setAttribute('tabindex', '-1');
    setTimeout(() => (node as HTMLElement).focus({ preventScroll: true }), 0);
  }
}

export function ValidationSummary({ issues }: ValidationSummaryProps): ReactNode {
  const [collapsed, setCollapsed] = useState(false);
  if (issues.length === 0) return null;

  return (
    <div
      data-validation-summary="true"
      role="region"
      aria-label={`${issues.length} item${issues.length === 1 ? '' : 's'} to review`}
      className="mb-3 border border-amber-200 bg-amber-50 rounded-sm"
    >
      <button
        type="button"
        aria-expanded={!collapsed}
        aria-controls="validation-summary-body"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
      >
        <span className="flex items-center gap-2">
          <span
            data-validation-summary-count
            className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[11px] font-semibold rounded-full bg-amber-600 text-white"
          >
            {issues.length}
          </span>
          <span className="text-sm font-medium text-amber-900">
            {issues.length === 1 ? 'item to review' : 'items to review'}
          </span>
        </span>
        <span aria-hidden="true" className="text-amber-700 text-xs">
          {collapsed ? 'Show' : 'Hide'}
        </span>
      </button>
      {!collapsed && (
        <ul
          id="validation-summary-body"
          role="list"
          className="border-t border-amber-200 divide-y divide-amber-100"
        >
          {issues.map((issue, idx) => (
            <li
              key={`${issue.fieldId}-${issue.kind}-${idx}`}
              role="listitem"
              data-validation-issue={issue.kind}
              data-issue-field-id={issue.fieldId}
            >
              <button
                type="button"
                onClick={() => focusField(issue.fieldId)}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-amber-100 flex items-center gap-2"
              >
                <span className="font-medium text-amber-900 truncate">{issue.label}</span>
                <span className="text-amber-700">— {describeIssueKind(issue.kind)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
